import React, { useReducer, useEffect, useState, useRef } from 'react';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Input,
} from '@material-ui/core';
import CustomTextField from '../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import ArrowBack from '@material-ui/icons/ArrowBack';
import './appConfiguration.scss';
import CustomPopupModel from '../../../shared/custom-popup/Custom-Popup';
import useOutsideAlerter from '../../../custom-hooks/useOutsideAlerter';
import { APP, TITLE } from './AppConfigurationConst';
import { decryptData, encryptData, validateAlphanumeric } from '../../../utils/formUtils';
import { decryptLocalStorage } from '../../../utils/authUtils';
import { notificationManager } from '../../../utils/NotificationUtils';

const CreateAppConfiguration = ({ handleAddAction, handleCancel, editMode, activeTab }) => {
  const [isFormValid, setFormValid] = useState(true);
  const [isShowPopup, setIsShowPopup] = useState(false);
  const [exitIntent, setExitIntent] = useState(null);
  const wrapperRef = useRef(null);
  const baselineRef = useRef(null);
  const restoreToBaseline = () => {
    const snap = baselineRef?.current;
    if (!snap) return;
    const draft = { ...formData };
    draft.name = {
      ...draft.name,
      value: snap?.name,
      errors: snap?.name ? {} : { required: 'Name is required.' },
      isError: !snap?.name,
    };
    draft.description = {
      ...draft.description,
      value: snap?.description,
      errors: snap?.description ? {} : { required: 'Description is required.' },
      isError: !snap?.description,
    };
    draft.channelType = {
      ...draft.channelType,
      value: snap?.channelType || 'Staff Connect',
      errors: {},
      isError: false,
    };
    const pwaRequired = snap?.channelType === 'PWA';
    let pwaErr = {};
    let pwaIsError = false;
    if (pwaRequired) {
      if (!snap?.pwaURLCode) {
        pwaErr = { required: 'pwaURLCode is required.' };
        pwaIsError = true;
      } else if (!validateAlphanumeric(snap?.pwaURLCode)) {
        pwaErr = { required: 'Only alphabets and numbers are accepted.' };
        pwaIsError = true;
      }
    }
    draft.pwaURLCode = {
      ...draft.pwaURLCode,
      value: snap?.pwaURLCode,
      errors: pwaErr,
      isError: pwaIsError,
    };
    let cfgErr = {};
    let cfgIsError = false;
    if (activeTab === APP && !isJson(snap?.configuration)) {
      cfgErr = { required: 'Please provide valid JSON' };
      cfgIsError = true;
    }
    draft.configuration = {
      ...draft.configuration,
      value: snap?.configuration,
      errors: cfgErr,
      isError: cfgIsError,
    };

    setFormData(draft);
    recomputeFormValidity(draft);
  };
  const takeSnapshot = (state) => ({
    name: (state?.name?.value || '')?.trim(),
    description: (state?.description?.value || '')?.trim(),
    configuration: (state?.configuration?.value || '')?.trim(),
    channelType: state?.channelType?.value || 'Staff Connect',
    pwaURLCode: (state?.pwaURLCode?.value || '')?.trim(),
  });
  const initialFormState = {
    name: {
      value: '',
      fieldName: 'Name',
      errors: {},
      validations: ['required'],
      isError: true,
    },
    description: {
      value: '',
      fieldName: 'Description',
      errors: {},
      validations: ['required'],
      isError: true,
    },
    channelType: {
      value: 'Staff Connect',
      fieldName: 'channelType',
      errors: {},
      validations: ['required'],
      isError: false,
    },
    pwaURLCode: {
      value: '',
      fieldName: 'PWA URL Code',
      errors: {},
      validations: ['required'],
      isError: false,
    },
    configuration: {
      value: '',
      fieldName: 'Configuration',
      errors: {},
      validations: ['required'],
      isError: true,
      allowHtml: true,
    },
  };
  const [formData, setFormData] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    initialFormState,
  );
  const resetFormData = () => {
    setFormData({ ...initialFormState });
    setFormValid(true);
  };
  const isJson = (data) => {
    try {
      JSON.parse(data);
    } catch (e) {
      return false;
    }
    return true;
  };
  const recomputeFormValidity = (draft) => {
    const errorThere = Object?.values(draft)?.find((f) => f?.isError);
    setFormValid(!!errorThere);
  };

  const handleOnChangeValidation = (value, fieldName) => {
    formData[fieldName] = value;
    const currentValue = formData[fieldName]?.value;
    const newFieldName = formData[fieldName]?.fieldName || fieldName;
    if (formData[fieldName]?.validations?.includes('required') && !currentValue?.trim?.('')) {
      formData[fieldName].errors = { required: `${newFieldName} is required.` };
      formData[fieldName].isError = true;
    } else if (['description', 'name'].includes(fieldName) && !validateAlphanumeric(currentValue)) {
      formData[fieldName].errors = {
        required: 'Only alphabets and numbers are accepted.',
      };
      formData[fieldName].isError = true;
    } else if (
      fieldName === 'configuration' &&
      activeTab === APP &&
      !isJson(formData?.configuration?.value)
    ) {
      formData.configuration.errors = { required: 'Please provide valid JSON' };
      formData[fieldName].isError = true;
    } else if (fieldName === 'pwaURLCode') {
      if (formData?.channelType?.value === 'PWA') {
        if (!currentValue?.trim?.('')) {
          formData[fieldName].errors = { required: `${newFieldName} is required.` };
          formData[fieldName].isError = true;
        } else if (!validateAlphanumeric(currentValue)) {
          formData[fieldName].errors = {
            required: 'Only alphabets and numbers are accepted.',
          };
          formData[fieldName].isError = true;
        } else {
          formData[fieldName].errors = {};
          formData[fieldName].isError = false;
        }
      } else {
        formData[fieldName].errors = {};
        formData[fieldName].isError = false;
      }
    } else if (fieldName === 'channelType') {
      formData[fieldName].errors = {};
      formData[fieldName].isError = false;
      if (formData?.channelType?.value === 'PWA') {
        const hc = formData.pwaURLCode?.value || '';
        if (!hc.trim('')) {
          formData.pwaURLCode.errors = { required: 'pwaURLCode is required.' };
          formData.pwaURLCode.isError = true;
        } else if (!validateAlphanumeric(hc)) {
          formData.pwaURLCode.errors = { required: 'Only alphabets and numbers are accepted.' };
          formData.pwaURLCode.isError = true;
        } else {
          formData.pwaURLCode.errors = {};
          formData.pwaURLCode.isError = false;
        }
      } else {
        formData.pwaURLCode.errors = {};
        formData.pwaURLCode.isError = false;
      }
    } else {
      formData[fieldName].errors = {};
      formData[fieldName].isError = false;
    }
    const draft = { ...formData };
    setFormData(draft);
    recomputeFormValidity(draft);
  };

  const handleValidation = () => {
    const configuration = process.env.REACT_APP_CONFIGURATION_KEY;
    const userName = decryptLocalStorage('user');
    const channel = formData?.channelType?.value;
    const payload = {
      name: formData?.name?.value,
      description: formData?.description?.value,
      configuration:
        activeTab === APP
          ? encryptData(formData?.configuration?.value, configuration)
          : formData?.configuration?.value,
      userName: userName?.firstName,
      channelType: channel,
      pwaURLCode: channel === 'PWA' ? (formData?.pwaURLCode?.value || '')?.trim() : '',
    };
    handleAddAction(payload);
    setIsShowPopup(false);
    resetFormData();
  };

  useEffect(() => {
    try {
      const configuration = process.env.REACT_APP_CONFIGURATION_KEY;
      if (editMode) {
        formData.configuration.value =
          activeTab === APP
            ? decryptData(editMode?.configuration, configuration)
            : editMode?.configuration;
        formData.configuration.isError = false;
        formData.name.value = editMode?.name || '';
        formData.name.isError = !formData?.name?.value;
        formData.description.value = editMode?.description || '';
        formData.description.isError = !formData?.description?.value;
        formData.channelType.value = editMode?.channelType || 'Staff Connect';
        formData.channelType.isError = false;
        formData.pwaURLCode.value = editMode?.pwaURLCode || '';
        if (formData.channelType.value === 'PWA') {
          formData.pwaURLCode.isError = !formData?.pwaURLCode?.value;
          formData.pwaURLCode.errors = formData?.pwaURLCode?.isError
            ? { required: 'pwaURLCode is required.' }
            : {};
        } else {
          formData.pwaURLCode.isError = false;
          formData.pwaURLCode.errors = {};
        }
        const draft = { ...formData };
        setFormData(draft);
        handleOnChangeValidation({ ...draft.channelType }, 'channelType');
        recomputeFormValidity(draft);
        baselineRef.current = takeSnapshot(draft);
      }
    } catch (error) {
      if (process.env.REACT_APP_ENVIRONMENT === 'dev') {
        console.log(error?.message);
      }
      notificationManager('info', 'Error while decrypting. Please re-enter your details.');
    }
  }, [editMode]);

  const currentSnap = takeSnapshot(formData);
  const baseSnap = baselineRef?.current || takeSnapshot(initialFormState);
  const isDirty = JSON?.stringify(currentSnap) !== JSON?.stringify(baseSnap);

  useOutsideAlerter(wrapperRef, isDirty, (flag) => {
    if (flag) {
      setExitIntent('outside');
      setIsShowPopup(true);
    } else {
      setIsShowPopup(false);
      setExitIntent(null);
    }
  });
  useEffect(() => {
    return () => {
      resetFormData();
    };
  }, []);

  useEffect(() => {
    if (!editMode) {
      baselineRef.current = takeSnapshot(initialFormState);
    }
  }, [editMode, activeTab]);

  return (
    <>
      <CustomPopupModel
        popUp={{
          status: isShowPopup,
          title: 'Do you want to save the changes?',
          description: '',
          buttonName: isFormValid ? '' : 'Save',
          cancel: 'Cancel',
        }}
        style={{
          paddingLeft: '2rem',
          paddingRight: '2rem',
        }}
        onClickCancel={() => {
          setIsShowPopup(false);
          if (editMode) {
            restoreToBaseline();
          } else {
            resetFormData();
          }
          if (exitIntent === 'back') {
            setExitIntent(null);
            handleCancel();
          } else {
            setExitIntent(null);
          }
        }}
        onClick={() => {
          handleValidation();
        }}
      />
      <div className='container mt-4 mb-3 detail-view-65 pb-4' ref={wrapperRef}>
        <div className='mt-5 row mx-0'>
          {' '}
          <div className='col-md-6 px-0 d-flex align-items-center'>
            <Button
              variant='text'
              color='secondary'
              size='small'
              startIcon={<ArrowBack />}
              onClick={() => {
                if (isDirty) {
                  setExitIntent('back');
                  setIsShowPopup(true);
                } else {
                  handleCancel();
                }
              }}
              className='me-2'
            >
              Back
            </Button>
            <span className='page-title'>
              {editMode ? 'Update' : 'Create'} {TITLE[activeTab]}
            </span>
          </div>
        </div>
        <div className='card shadow mt-5 mb-4 h-100 mx-4 pb-5 px-4'>
          <div className='mt-3 row'>
            <div className='col-md-6 col-sm-12'>
              <CustomTextField
                item={formData.name}
                handleInputChange={(e) => {
                  handleOnChangeValidation(e, 'name');
                }}
                maxLength={100}
                characterCount={true}
                endAdornment={true}
              />
            </div>
            <div className=' col-md-6 col-sm-12'>
              <CustomTextField
                item={formData.description}
                handleInputChange={(e) => {
                  handleOnChangeValidation(e, 'description');
                }}
                maxLength={100}
                characterCount={true}
                endAdornment={true}
              />
            </div>
          </div>
          <div className='mt-4 row'>
            <div className='col-md-6 col-sm-12' style={{ paddingTop: 14 }}>
              <FormControl className='w-100'>
                <Select
                  value={formData?.channelType?.value}
                  onChange={(e) => {
                    const val = e?.target?.value;
                    formData.channelType.value = val;
                    formData.channelType.errors = {};
                    if (val !== 'PWA') {
                      formData.pwaURLCode.value = '';
                      formData.pwaURLCode.errors = {};
                      formData.pwaURLCode.isError = false;
                    }
                    const draft = { ...formData };
                    setFormData(draft);
                    handleOnChangeValidation(draft.channelType, 'channelType');
                  }}
                  input={<Input />}
                  displayEmpty
                  className='select-plain'
                >
                  <MenuItem value='Staff Connect'>Staff Connect</MenuItem>
                  <MenuItem value='PWA'>PWA</MenuItem>
                </Select>

                {formData?.channelType?.errors?.required && (
                  <FormHelperText error>{formData?.channelType?.errors?.required}</FormHelperText>
                )}
              </FormControl>
            </div>

            {formData?.channelType?.value === 'PWA' && (
              <div className='col-md-6 col-sm-12'>
                <CustomTextField
                  item={formData?.pwaURLCode}
                  handleInputChange={(e) => {
                    handleOnChangeValidation(e, 'pwaURLCode');
                  }}
                  maxLength={50}
                  characterCount={true}
                  endAdornment={true}
                />
              </div>
            )}
          </div>
          <div className='mt-5'>
            <TextField
              id='outlined-basic'
              label={activeTab === APP ? 'Configuration' : 'CSS'}
              variant='outlined'
              className='configurationTextFiled w-100'
              required={true}
              multiline={true}
              minRows={10}
              helperText={formData?.configuration?.errors?.required}
              error={!!formData?.configuration?.errors?.required}
              value={formData?.configuration?.value}
              onChange={(e) => {
                formData.configuration.value = e.target.value;
                formData.configuration.errors = {};
                handleOnChangeValidation(formData.configuration, 'configuration');
              }}
            />
          </div>
          <div className='w-100 mt-5'>
            <Button
              onClick={() => {
                resetFormData();
                handleCancel();
              }}
              variant='outlined'
              type='button'
              className='me-3'
              size='large'
            >
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                setIsShowPopup(true);
              }}
              variant='contained'
              type='submit'
              color={'primary'}
              className='me-3'
              size='large'
              disabled={isFormValid}
            >
              {editMode ? 'Update' : 'Add'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateAppConfiguration;
